import React, { ButtonHTMLAttributes } from "react";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Defines the visual style of the button.
   */
  variant?: "primary" | "secondary" | "outline" | "outlineDark" | "ghost";
  /**
   * Defines the size (padding and text size) of the button.
   */
  size?: "sm" | "md" | "lg";
  /**
   * Defines the border radius of the button.
   */
  shape?: "rounded" | "full" | "default";
  className?: string;
  /**
   * Optional icon to display inside the button.
   */
  icon?: React.ReactNode;
  /**
   * Position of the icon relative to the text.
   */
  iconPosition?: "left" | "right";
}

export default function Button({
  className = "",
  variant = "primary",
  size = "md",
  shape = "full",
  icon,
  iconPosition = "left",
  children,
  ...props
}: ButtonProps) {
  const baseClasses =
    "inline-flex items-center justify-center gap-2 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

  const variantClasses = {
    primary: "bg-accent text-primary hover:bg-accent-hover",
    secondary: "bg-primary text-white hover:bg-primary-hover",
    outline: "border border-white/40 bg-black text-white hover:bg-white/10",
    outlineDark:
      "border border-gray-300 bg-white text-slate-700 hover:bg-gray-50",
    ghost: "bg-transparent text-slate-700 hover:bg-black/5",
  };

  const sizeClasses = {
    sm: "px-3 py-2 text-xs md:px-5 md:text-sm",
    md: "px-4 py-2 text-sm md:px-6 md:py-3",
    lg: "px-7 py-3 text-sm md:text-lg",
  };

  const shapeClasses = {
    rounded: "rounded-lg",
    full: "rounded-full",
    default: "rounded-md",
  };

  const vClass = variantClasses[variant] || variantClasses.primary;
  const sClass = sizeClasses[size] || sizeClasses.md;
  const shClass = shapeClasses[shape] || shapeClasses.full;

  const combinedClasses =
    `${baseClasses} ${vClass} ${sClass} ${shClass} ${className}`.trim();

  return (
    <button className={combinedClasses} {...props}>
      {icon && iconPosition === "left" && icon}
      {children}
      {icon && iconPosition === "right" && icon}
    </button>
  );
}
